import type * as React from 'react'

import type {TreeEvent} from './Sidebar.types'
import {TreeEventType} from './Sidebar.types'

type Item = {
  level: number
  expanded?: boolean | null
  focusable?: boolean
}
type Items<FlatTreeItem extends Item> = ReadonlyArray<FlatTreeItem>
enum Key {
  HOME = 'Home',
  END = 'End',
  UP = 'ArrowUp',
  DOWN = 'ArrowDown',
  LEFT = 'ArrowLeft',
  RIGHT = 'ArrowRight',
  ENTER = 'Enter',
  SPACE = ' ',
  PAGE_UP = 'PageUp',
  PAGE_DOWN = 'PageDown',
}

function getPrevExistingIndex<FlatTreeItem extends Item>(
  items: Items<FlatTreeItem>,
  currentIndex: number,
): number {
  let prevIndex = currentIndex - 1

  while (prevIndex >= 0) {
    if (items[prevIndex]?.focusable) {
      return prevIndex
    }

    prevIndex--
  }

  return -1
}

function getPageBackIndex<FlatTreeItem extends Item>(
  items: Items<FlatTreeItem>,
  currentIndex: number,
  pageStartIndex: number,
  pageEndIndex: number,
) {
  const pageSize = pageEndIndex - pageStartIndex
  const pageBackIndex = currentIndex - pageSize
  return getNextExistingIndex(items, pageBackIndex < 0 ? 0 : pageBackIndex)
}

function getPageForwardIndex<FlatTreeItem extends Item>(
  items: Items<FlatTreeItem>,
  currentIndex: number,
  pageStartIndex: number,
  pageEndIndex: number,
) {
  const pageSize = pageEndIndex - pageStartIndex
  const pageForwardIndex = currentIndex + pageSize
  return getPrevExistingIndex(
    items,
    pageForwardIndex > items.length - 1 ? items.length - 1 : pageForwardIndex,
  )
}

export function getNextExistingIndex<FlatTreeItem extends Item>(
  items: Items<FlatTreeItem>,
  currentIndex: number,
): number {
  const lastIndex = items.length - 1
  let nextIndex = currentIndex + 1

  while (nextIndex <= lastIndex) {
    if (items[nextIndex]?.focusable) {
      return nextIndex
    }

    nextIndex++
  }

  return lastIndex + 1
}

function getParentIndex<FlatTreeItem extends Item>(
  items: Items<FlatTreeItem>,
  currentIndex: number,
): number | null | undefined {
  const currentItem = items[currentIndex]

  if (currentItem == null || currentItem.level === 0) {
    return null
  }

  let parentIndex = currentIndex
  let parentItem = currentItem

  while (parentIndex > 0 && parentItem.level >= currentItem.level) {
    parentIndex -= 1
    parentItem = items[parentIndex]
  }

  return parentIndex > -1 ? parentIndex : null
}

function keyboardToEditTreeEvent<FlatTreeItem extends Item>(
  event: React.KeyboardEvent<EventTarget>,
  items: Items<FlatTreeItem>,
  focusedIndex: number,
): TreeEvent | null | undefined {
  const focusedItem = items[focusedIndex]

  if (focusedItem == null) {
    return null
  }

  let treeEvent: TreeEvent | null | undefined = null
  const modifier = navigator.platform === 'MacIntel' ? event.metaKey : event.ctrlKey

  if (modifier && event.key === Key.UP) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_MOVE_UP,
      index: focusedIndex,
    }
  }

  if (modifier && event.key === Key.DOWN) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_MOVE_DOWN,
      index: focusedIndex,
    }
  }

  if (treeEvent != null) {
    event.preventDefault()
  }

  return treeEvent
}

function keyboardToTreeEvent<FlatTreeItem extends Item>(
  event: React.KeyboardEvent<EventTarget>,
  items: Items<FlatTreeItem>,
  focusedIndex: number,
  pageStartIndex?: number,
  pageEndIndex?: number,
): TreeEvent | null | undefined {
  const focusedItem = items[focusedIndex]

  if (focusedItem == null) {
    return null
  }

  let treeEvent: TreeEvent | null | undefined = null
  const modifier = navigator.platform === 'MacIntel' ? event.metaKey : event.ctrlKey

  if (event.key === Key.HOME) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_FOCUS,
      index: getNextExistingIndex(items, -1),
    }
  }

  if (event.key === Key.END) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_FOCUS,
      index: getPrevExistingIndex(items, items.length),
    }
  }

  if (event.key === Key.UP) {
    treeEvent = modifier
      ? {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getNextExistingIndex(items, 0),
        }
      : {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getPrevExistingIndex(items, focusedIndex),
        }
  }

  if (event.key === Key.DOWN) {
    treeEvent = modifier
      ? {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getPrevExistingIndex(items, items.length - 1),
        }
      : {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getNextExistingIndex(items, focusedIndex),
        }
  }

  if (event.key === Key.LEFT) {
    if (focusedItem.expanded === true) {
      treeEvent = {
        type: TreeEventType.EVENT_TYPE_COLLAPSE,
        index: focusedIndex,
      }
    } else {
      const parentIndex = getParentIndex(items, focusedIndex)

      if (focusedItem.level > 0 && parentIndex != null) {
        treeEvent = {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: parentIndex,
        }
      } else {
        treeEvent = {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getPrevExistingIndex(items, focusedIndex),
        }
      }
    }
  }

  if (event.key === Key.RIGHT) {
    if (focusedItem.expanded === false) {
      treeEvent = {
        type: TreeEventType.EVENT_TYPE_EXPAND,
        index: focusedIndex,
      }
    } else {
      treeEvent = {
        type: TreeEventType.EVENT_TYPE_FOCUS,
        index: getNextExistingIndex(items, focusedIndex),
      }
    }
  }

  if (event.key === Key.SPACE) {
    treeEvent = event.shiftKey
      ? {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getPrevExistingIndex(items, focusedIndex),
        }
      : {
          type: TreeEventType.EVENT_TYPE_FOCUS,
          index: getNextExistingIndex(items, focusedIndex),
        }
  }

  if (event.key === Key.PAGE_UP && pageStartIndex != null && pageEndIndex != null) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_FOCUS,
      index: getPageBackIndex(items, focusedIndex, pageStartIndex, pageEndIndex),
    }
  }

  if (event.key === Key.PAGE_DOWN && pageStartIndex != null && pageEndIndex != null) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_FOCUS,
      index: getPageForwardIndex(items, focusedIndex, pageStartIndex, pageEndIndex),
    }
  }

  if (event.key === Key.ENTER) {
    treeEvent = {
      type: TreeEventType.EVENT_TYPE_SELECT,
      index: focusedIndex,
    }
  }

  if (treeEvent != null) {
    event.preventDefault()
  }

  return treeEvent
}

export {keyboardToTreeEvent, keyboardToEditTreeEvent}
